/* ─────────────────────────────────────────────────────────────────────────
   Handles and organisation slugs — the grammar, in one place.

   This file is **owned here and copied verbatim into website-enterprise,
   relay-server, website-cloud and website-lms**, the same arrangement
   `entitlements.ts` has and for the same reason: every one of those Workers
   enforces these rules and they have to agree. website-main validates a
   handle when it creates the account; website-enterprise validates a slug
   when it creates the organisation, and a handle before it asks for an
   account. Change it here first, then propagate with
   `node scripts/sync-shared.mjs` from the workspace repo; every copy is
   pinned by `shared/copies.test.ts`.

   ── The rule everything else follows from ────────────────────────────────
   `timurinal`, `timurinal.northgate` and `timurinal.mauvely` are **three
   different accounts**. The handle is the whole string. There is no sense in
   which the part before the dot is "the same user with an organisation
   attached", and no lookup anywhere may resolve `timurinal` to one of the
   others.

   That has to be true in the database, not merely in the code that reads it: a
   single unique column over the full handle, not a `(name, org)` pair with a
   constraint on the pair. Otherwise someone eventually writes a lookup that
   resolves the bare name to whichever row it found first. See website-main's
   migration 0017.

   ── Case ────────────────────────────────────────────────────────────────
   Handles and slugs are lowercase. `normalise` folds and trims, and the
   uniqueness index is over the folded form, so `Timurinal` and `timurinal`
   cannot both exist. Nothing displays a handle in the casing it was typed,
   because there is no casing: it is an identifier, not a name.
   ───────────────────────────────────────────────────────────────────────── */

/**
 * Names nobody may take, enforced at creation.
 *
 * Cheap now, impossible later — a list applied after the fact is a list of
 * accounts to take away from people. Short on purpose: every entry has to be a
 * name that would be *mistaken for us*, not merely one we might want.
 */
export const RESERVED_HANDLES: readonly string[] = [
  'admin',
  'support',
  'help',
  'mauvely',
  // Assist, who answers as @assist in Relay.
  'assist',
  'security',
  'billing',
  'system',
  'root'
];

/**
 * Names no organisation may take as its slug.
 *
 * A superset of the handle list, because a slug becomes a hostname —
 * `<slug>.cloud.mauvely.com` — and a hostname label has its own set of things
 * that must not be claimable. `www` and `mail` are not names anyone would
 * mistake for Mauvely support; they are names a browser and a mail server
 * treat specially.
 */
export const RESERVED_SLUGS: readonly string[] = [
  ...RESERVED_HANDLES,
  'www',
  'api',
  'app',
  'mail',
  'smtp',
  'ns',
  'cloud',
  'enterprise',
  'internal',
  'status',
  'docs',
  'courses',
  'review',
  'relay',
  'scope',
  'lms',
  'learn',
  'player'
];

/** 2–32 characters, lowercase alphanumeric and hyphens, no hyphen at either
 *  end. The shape of a DNS label, because that is what a slug becomes — with a
 *  floor of two, because a single-character subdomain is a squat rather than a
 *  name. */
const SLUG_RE = /^[a-z0-9][a-z0-9-]{0,30}[a-z0-9]$/;

/**
 * The name half of a handle: 3–32 characters, same alphabet as a slug.
 *
 * Longer minimum than a slug because a two-character handle is not a person's
 * name, it is a squat.
 *
 * Written without an optional group, and that is the point: `[a-z0-9](?:…)?`
 * reads as "3 to 32" and accepts **one** character, because the group it makes
 * optional is the only thing carrying the minimum. Three required parts say
 * three, and say it to the regex as well as to the reader.
 */
const HANDLE_NAME_RE = /^[a-z0-9][a-z0-9-]{1,30}[a-z0-9]$/;

export interface HandleParts {
  /** The whole string, folded. This is what is stored and what is unique. */
  handle: string;
  /** The part before the dot. Never unique on its own. */
  name: string;
  /** The part after the dot, or null for an individual account. */
  orgSlug: string | null;
}

export type HandleError =
  | 'empty'
  | 'too_long'
  | 'bad_name'
  | 'bad_org'
  | 'too_many_parts'
  | 'reserved';

export const normalise = (v: string): string => v.trim().toLowerCase();

/** True when a string is a legal organisation slug and not on the reserved
 *  list. Does not check that the slug is free — that is a database question. */
export function isValidSlug(v: string): boolean {
  const s = normalise(v);
  return SLUG_RE.test(s) && !RESERVED_SLUGS.includes(s);
}

/**
 * Split a handle into its parts, or say what is wrong with it.
 *
 * Returns a discriminated result rather than throwing, because the two callers
 * want different errors: website-main raises an `ApiError` the admin sees, and
 * website-enterprise checks a candidate before it spends a provisioning call.
 *
 * What this deliberately does **not** check is whether `orgSlug` names a real
 * organisation. That is a database question and it is asked in exactly one
 * place — the provisioning entrypoint — because a handle that claims an org is
 * a claim about a relationship, and only the side that holds the membership
 * row can settle it.
 */
export function parseHandle(input: string): { ok: true; parts: HandleParts } | { ok: false; error: HandleError } {
  const handle = normalise(input);

  if (!handle) return { ok: false, error: 'empty' };
  // 32 + '.' + 32. Bounded before anything splits it, so a pathological input
  // never reaches a regex.
  if (handle.length > 65) return { ok: false, error: 'too_long' };

  const parts = handle.split('.');
  if (parts.length > 2) return { ok: false, error: 'too_many_parts' };

  const name = parts[0] ?? '';
  const orgSlug = parts.length === 2 ? (parts[1] ?? '') : null;

  if (!HANDLE_NAME_RE.test(name)) return { ok: false, error: 'bad_name' };
  if (orgSlug !== null && !SLUG_RE.test(orgSlug)) return { ok: false, error: 'bad_org' };

  // The reserved list applies to the **name** half only. `ada.support` is an
  // ordinary person at a company whose slug is `support`; refusing it would be
  // refusing a name for a collision that does not exist, since the handle is
  // the whole string. `support` on its own is the one that could be mistaken
  // for us.
  if (orgSlug === null && RESERVED_HANDLES.includes(name)) {
    return { ok: false, error: 'reserved' };
  }

  return { ok: true, parts: { handle, name, orgSlug } };
}

/** A message an admin can act on, per failure. One sentence, no reasoning. */
export const HANDLE_ERROR_MESSAGE: Record<HandleError, string> = {
  empty: 'Enter a username.',
  too_long: 'That username is too long.',
  bad_name: 'Use 3 to 32 letters, numbers or hyphens, starting and ending with one.',
  bad_org: 'The part after the dot is not a valid organisation name.',
  too_many_parts: 'A username has at most one dot.',
  reserved: 'That username is reserved.'
};

/**
 * What to show somebody in place of their address.
 *
 * A provisioned account has no mailbox. `users.email` is NOT NULL with a
 * unique index, so it holds a derived address under `handle.mauvely.invalid`
 * — a real value, never deliverable, and never something to put in front of a
 * person. `timuri.suez@handle.mauvely.invalid` is not who they are;
 * `timuri.suez` is.
 *
 * The flag rather than a `.invalid` test: `users.email_is_placeholder` is what
 * the writer recorded, and sniffing the domain instead would be a second
 * definition of the same fact that could disagree with it.
 *
 * Falls back to the address when there is no handle, because an account with
 * neither has nothing else to show and an empty identity line is worse than an
 * unhelpful one.
 */
export function displayIdentity(user: {
  handle?: string | null;
  email: string;
  emailIsPlaceholder?: boolean | null;
}): string {
  return user.emailIsPlaceholder && user.handle ? user.handle : user.email;
}
